import React, { useState } from 'react';
import { Trash2Icon, MinusIcon, SparklesIcon } from 'lucide-react';
interface AngleRange {
  id: number;
  start: string;
  end: string;
}
interface Nozzle {
  diameter: string;
  flow: number;
  wetRange: string;
  requiredFlow: number;
}
interface EndGunData {
  brand: string;
  model: string;
  nozzle: string;
  autoCalculateNozzle: boolean;
  angleRanges: AngleRange[];
}
export function EndGunTab() {
  const [selectedEndGun, setSelectedEndGun] = useState<1 | 2>(1);
  // Separate state for each end gun
  const [endGun1Data, setEndGun1Data] = useState<EndGunData>({
    brand: 'Komet',
    model: 'KPW HEAD-UP',
    nozzle: '18 mm - 0.71"',
    autoCalculateNozzle: false,
    angleRanges: [{
      id: 1,
      start: '0',
      end: '270'
    }, {
      id: 2,
      start: '280',
      end: '320'
    }]
  });
  const [endGun2Data, setEndGun2Data] = useState<EndGunData>({
    brand: 'Komet',
    model: 'KPW HEAD-UP',
    nozzle: '16 mm - 0.63"',
    autoCalculateNozzle: false,
    angleRanges: [{
      id: 1,
      start: '0',
      end: '180'
    }]
  });
  const [showNozzleSelector, setShowNozzleSelector] = useState(false);
  // Get current end gun data
  const currentData = selectedEndGun === 1 ? endGun1Data : endGun2Data;
  const setCurrentData = selectedEndGun === 1 ? setEndGun1Data : setEndGun2Data;
  const brands = ['Komet', 'Nelson', 'Senninger'];
  const modelsByBrand: {
    [key: string]: string[];
  } = {
    Komet: ['Twin Max', 'SR101', 'KPW HEAD-UP', 'KPW HEAD-DOWN'],
    Nelson: ['S3000', 'R3000', 'D3000'],
    Senninger: ['i-Wob', 'Super Spray', 'Quad Spray']
  };
  const availableNozzles: Nozzle[] = [{
    diameter: '10 mm - 0.39"',
    flow: 8.5,
    wetRange: '7.2 / 8.9',
    requiredFlow: 9.2
  }, {
    diameter: '11 mm - 0.43"',
    flow: 9.8,
    wetRange: '7.8 / 9.5',
    requiredFlow: 10.1
  }, {
    diameter: '12 mm - 0.47"',
    flow: 10.9,
    wetRange: '8.1 / 9.9',
    requiredFlow: 10.8
  }, {
    diameter: '13 mm - 0.51"',
    flow: 11.5,
    wetRange: '8.5 / 10.3',
    requiredFlow: 11.0
  }, {
    diameter: '14 mm - 0.55"',
    flow: 11.9,
    wetRange: '8.7 / 10.6',
    requiredFlow: 11.3
  }, {
    diameter: '15 mm - 0.59"',
    flow: 12.0,
    wetRange: '8.8 / 10.8',
    requiredFlow: 11.4
  }, {
    diameter: '16 mm - 0.63"',
    flow: 12.1,
    wetRange: '8.9 / 10.9',
    requiredFlow: 11.5
  }, {
    diameter: '17 mm - 0.67"',
    flow: 12.2,
    wetRange: '8.9 / 10.9',
    requiredFlow: 11.5
  }, {
    diameter: '18 mm - 0.71"',
    flow: 12.2,
    wetRange: '8.9 / 10.9',
    requiredFlow: 11.5
  }];
  const currentNozzle = availableNozzles.find(n => n.diameter === currentData.nozzle) || availableNozzles[8];
  const coverage = currentData.angleRanges.reduce((sum, range) => {
    const start = parseInt(range.start);
    const end = parseInt(range.end);
    return sum + (end - start);
  }, 0);
  const removeAngleRange = (id: number) => {
    setCurrentData({
      ...currentData,
      angleRanges: currentData.angleRanges.filter(range => range.id !== id)
    });
  };
  const updateAngleRange = (id: number, field: 'start' | 'end', value: string) => {
    setCurrentData({
      ...currentData,
      angleRanges: currentData.angleRanges.map(range => range.id === id ? {
        ...range,
        [field]: value
      } : range)
    });
  };
  const isSegmentActive = (segmentStart: number, segmentEnd: number) => {
    return currentData.angleRanges.some(range => {
      const start = parseInt(range.start);
      const end = parseInt(range.end);
      return segmentStart >= start && segmentStart < end || segmentEnd > start && segmentEnd <= end || start >= segmentStart && start < segmentEnd;
    });
  };
  return <div className="space-y-6" data-id="element-6">
      {/* End Gun Selection Toggle */}
      <div data-id="element-7">
        <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-8">
          Select End Gun
        </label>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg" data-id="element-9">
          <button onClick={() => setSelectedEndGun(1)} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${selectedEndGun === 1 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-10">
            End Gun 1
          </button>
          <button onClick={() => setSelectedEndGun(2)} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${selectedEndGun === 2 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-11">
            End Gun 2
          </button>
        </div>
      </div>
      {/* End Gun Info Card */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 shadow-lg text-white" data-id="element-12">
        <div className="flex items-center gap-3 mb-4" data-id="element-13">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-14">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" data-id="element-15">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" data-id="element-16" />
            </svg>
          </div>
          <h3 className="text-xl font-bold" data-id="element-17">End Gun {selectedEndGun}</h3>
        </div>
        <div className="space-y-3" data-id="element-18">
          <div className="flex justify-between items-center" data-id="element-19">
            <span className="text-blue-100 text-sm" data-id="element-20">Brand | Model:</span>
            <span className="font-semibold" data-id="element-21">
              {currentData.brand} | {currentData.model}
            </span>
          </div>
          <div className="flex justify-between items-center" data-id="element-22">
            <span className="text-blue-100 text-sm" data-id="element-23">Nozzle:</span>
            <span className="font-semibold" data-id="element-24">{currentData.nozzle}</span>
          </div>
          <div className="flex justify-between items-center" data-id="element-25">
            <span className="text-blue-100 text-sm" data-id="element-26">Nozzle Flow:</span>
            <span className="font-semibold" data-id="element-27">{currentNozzle.flow} m³/h</span>
          </div>
          <div className="flex justify-between items-center" data-id="element-28">
            <span className="text-blue-100 text-sm" data-id="element-29">Wet Range:</span>
            <span className="font-semibold" data-id="element-30">{currentNozzle.wetRange} m</span>
          </div>
          <div className="flex justify-between items-center" data-id="element-31">
            <span className="text-blue-100 text-sm" data-id="element-32">Required Flow:</span>
            <span className="font-semibold" data-id="element-33">
              {currentNozzle.requiredFlow} m³/h
            </span>
          </div>
          <div className="flex justify-between items-center" data-id="element-34">
            <span className="text-blue-100 text-sm" data-id="element-35">Coverage:</span>
            <span className="font-semibold" data-id="element-36">{coverage}°</span>
          </div>
        </div>
      </div>
      {/* Brand and Model Selection */}
      <div className="space-y-4" data-id="element-37">
        <div data-id="element-38">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-39">
            Brand
          </label>
          <select value={currentData.brand} onChange={e => {
          setCurrentData({
            ...currentData,
            brand: e.target.value,
            model: modelsByBrand[e.target.value][0]
          });
        }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-40">
            {brands.map(brand => <option key={brand} value={brand} data-id="element-41">
                {brand}
              </option>)}
          </select>
        </div>
        <div data-id="element-42">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-43">
            Model
          </label>
          <select value={currentData.model} onChange={e => setCurrentData({
          ...currentData,
          model: e.target.value
        })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-44">
            {modelsByBrand[currentData.brand].map(model => <option key={model} value={model} data-id="element-45">
                {model}
              </option>)}
          </select>
        </div>
      </div>
      {/* Nozzle Selection */}
      <div className="space-y-3" data-id="element-46">
        <label className="block text-sm font-medium text-gray-700" data-id="element-47">
          Nozzle Selection
        </label>
        <label className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-48">
          <input type="checkbox" checked={currentData.autoCalculateNozzle} onChange={e => setCurrentData({
          ...currentData,
          autoCalculateNozzle: e.target.checked
        })} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-49" />
          <div className="flex items-center gap-2 flex-1" data-id="element-50">
            <SparklesIcon className="w-5 h-5 text-blue-600" data-id="element-51" />
            <span className="text-sm font-medium text-gray-700" data-id="element-52">
              Auto-calculate optimal nozzle
            </span>
          </div>
        </label>
        {/* Show optimal nozzle when auto-calculate is enabled */}
        {currentData.autoCalculateNozzle && <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-53">
            <div className="flex items-center gap-2 mb-2" data-id="element-54">
              <SparklesIcon className="w-4 h-4 text-green-600" data-id="element-55" />
              <span className="text-sm font-semibold text-green-800" data-id="element-56">
                Calculated Optimal Nozzle
              </span>
            </div>
            <div className="text-sm text-green-900" data-id="element-57">
              <p className="font-medium" data-id="element-58">{availableNozzles[5].diameter}</p>
              <div className="mt-2 space-y-1 text-xs" data-id="element-59">
                <p data-id="element-60">Flow: {availableNozzles[5].flow} m³/h</p>
                <p data-id="element-61">Wet Range: {availableNozzles[5].wetRange} m</p>
                <p data-id="element-62">Required Flow: {availableNozzles[5].requiredFlow} m³/h</p>
              </div>
            </div>
          </div>}
        {!currentData.autoCalculateNozzle && <div data-id="element-63">
            <button onClick={() => setShowNozzleSelector(!showNozzleSelector)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-50 transition-colors" data-id="element-64">
              {currentData.nozzle}
            </button>
            {showNozzleSelector && <div className="mt-2 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden" data-id="element-65">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-66">
                  <p className="text-sm font-medium text-gray-700" data-id="element-67">
                    Select a nozzle:
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto" data-id="element-68">
                  <table className="w-full" data-id="element-69">
                    <thead className="bg-gray-50 sticky top-0" data-id="element-70">
                      <tr data-id="element-71">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-72">
                          Diameter
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600" data-id="element-73">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-74">
                      {availableNozzles.map(nozzle => <tr key={nozzle.diameter} className={`hover:bg-blue-50 transition-colors ${currentData.nozzle === nozzle.diameter ? 'bg-blue-100' : ''}`} data-id="element-75">
                          <td className="px-4 py-3 text-sm text-gray-900" data-id="element-76">
                            {nozzle.diameter}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-77">
                            <button onClick={() => {
                      setCurrentData({
                        ...currentData,
                        nozzle: nozzle.diameter
                      });
                      setShowNozzleSelector(false);
                    }} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-78">
                              Select
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>}
          </div>}
      </div>
      {/* Angle Range Configuration */}
      <div className="space-y-3" data-id="element-79">
        <label className="block text-sm font-medium text-gray-700" data-id="element-80">
          Angle Ranges
        </label>
        {currentData.angleRanges.map(range => <div key={range.id} className="flex items-center gap-2" data-id="element-81">
            <input type="number" value={range.start} onChange={e => updateAngleRange(range.id, 'start', e.target.value)} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center" data-id="element-82" />
            <span className="text-gray-600" data-id="element-83">°</span>
            <input type="number" value={range.end} onChange={e => updateAngleRange(range.id, 'end', e.target.value)} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center" data-id="element-84" />
            <span className="text-gray-600" data-id="element-85">°</span>
            <button onClick={() => removeAngleRange(range.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-86">
              <MinusIcon className="w-4 h-4" data-id="element-87" />
            </button>
          </div>)}
      </div>
      {/* Circular Degree Diagram */}
      <div className="flex flex-col items-center justify-center py-6" data-id="element-88">
        <div className="relative w-64 h-64" data-id="element-89">
          <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-90">
            <circle cx="100" cy="100" r="70" fill="none" stroke="#e5e7eb" strokeWidth="30" data-id="element-91" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, index) => {
            const nextAngle = index < 7 ? angle + 45 : 360;
            const isActive = isSegmentActive(angle, nextAngle);
            const startAngle = (angle - 90) * (Math.PI / 180);
            const endAngle = (nextAngle - 90) * (Math.PI / 180);
            const largeArc = 0;
            const x1 = 100 + 70 * Math.cos(startAngle);
            const y1 = 100 + 70 * Math.sin(startAngle);
            const x2 = 100 + 70 * Math.cos(endAngle);
            const y2 = 100 + 70 * Math.sin(endAngle);
            return isActive ? <path key={angle} d={`M 100 100 L ${x1} ${y1} A 70 70 0 ${largeArc} 1 ${x2} ${y2} Z`} fill="#6366f1" opacity="0.7" data-id="element-92" /> : null;
          })}
            <circle cx="100" cy="100" r="55" fill="white" data-id="element-93" />
            {[{
            angle: 0,
            label: '0°'
          }, {
            angle: 45,
            label: '45°'
          }, {
            angle: 90,
            label: '90°'
          }, {
            angle: 135,
            label: '135°'
          }, {
            angle: 180,
            label: '180°'
          }, {
            angle: 225,
            label: '225°'
          }, {
            angle: 270,
            label: '270°'
          }, {
            angle: 315,
            label: '315°'
          }].map(({
            angle,
            label
          }) => {
            const radian = (angle - 90) * Math.PI / 180;
            const x = 100 + 90 * Math.cos(radian);
            const y = 100 + 90 * Math.sin(radian);
            return <text key={angle} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="text-xs font-semibold fill-gray-700" data-id="element-94">
                  {label}
                </text>;
          })}
          </svg>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-95">
            <div className="w-6 h-6 border-2 border-white rounded-full flex items-center justify-center" data-id="element-96">
              <div className="w-2 h-2 bg-white rounded-full" data-id="element-97"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Delete End Gun Button */}
      <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-red-50 text-red-600 border border-gray-300 rounded-lg font-medium transition-colors" data-id="element-98">
        <Trash2Icon className="w-4 h-4" data-id="element-99" />
        Delete End Gun {selectedEndGun}
      </button>
    </div>;
}